import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { dataMap } from "@/constants/distribution";

const LineChartComponent = (props: any) => {
  const {
    chartData,            // 保留：兼容旧单任务
    multiTaskData,        // 新增：多任务数据 [{taskId, taskName, data:[]}]
    title,
    currentMode,
    height = 400,
    width = "100%",
    min,
    max,
    grid,
    extraText = null,
    unit,                 // 自定义Y轴单位（覆盖dataMap默认单位）
  } = props;

  // 使用 useMemo 缓存 ECharts 配置项
  const chartOption = useMemo(() => {
    // ==============================
    // 🔴 核心：自动判断 单任务 / 多任务
    // ==============================
    let series: any[] = [];
    let xData: any[] = [];

    // 多任务模式（优先）
    if (multiTaskData && Array.isArray(multiTaskData) && multiTaskData.length > 0) {
      const taskCount = multiTaskData.length;

      // 多配色（自动循环）
      const colors = ["#1890ff", "#ffa318", "#52c41a", "#eb2f96", "#13c2c2", "#f5222d", "#722ed1"];

      // X轴：取第一个任务的塔板数
      xData = multiTaskData[0]?.data?.map((_: any, index: number) => `${index + 1}`) || [];

      // 为每个任务生成一条线
      series = multiTaskData.map((task, idx) => {
        const color = colors[idx % colors.length];
        return {
          name: task.taskName,
          data: task.data || [],
          type: "line",
          symbol: "none",
          lineStyle: { color, width: 2 },
          itemStyle: { color },
          emphasis: { lineStyle: { width: 3 } },
        };
      });
    }
    // 原有单任务模式
    else if (chartData && chartData.length > 0) {
      xData = chartData.map((_: any, index: number) => `${index + 1}`);
      series = [
        {
          data: chartData,
          type: "line",
          symbol: "none",
          lineStyle: { color: "#1890ff", width: 2 },
          itemStyle: { color: "#1890ff" },
          emphasis: { lineStyle: { width: 3 } },
        },
      ];
    }

    return {
      title: {
        text: `${extraText ? extraText + "：" : ""}${title}`,
        left: "center",
        top: 15,
        textStyle: { fontSize: 16, fontWeight: 500 },
      },
      legend: {
        // 多任务自动显示图例
        show: multiTaskData && multiTaskData.length > 1,
        type: "scroll",
        top: 45,
        textStyle: { fontSize: 12 },
      },
      grid: {
        left: grid?.left ?? 80,
        right: grid?.right ?? 60,
        bottom: grid?.bottom ?? 20,
        top: grid?.top ?? (multiTaskData?.length > 1 ? 100 : 60),
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xData,
        boundaryGap: false,
        name: "塔板",
        nameTextStyle: { fontSize: 12, color: "rgba(0,0,0,0.6)" },
        splitLine: { show: true, lineStyle: { color: "rgba(0,0,0,0.1)" } },
        axisLine: { lineStyle: { color: "rgba(0,0,0,0.6)" } },
        axisLabel: { fontSize: 12 },
      },
      yAxis: {
        type: "value",
        name: unit || dataMap[currentMode]?.unit || "",
        nameTextStyle: { fontSize: 12, color: "rgba(0,0,0,0.6)" },
        splitNumber: 12,
        axisTick: { show: true },
        splitLine: { show: true, lineStyle: { color: "rgba(0,0,0,0.1)" } },
        axisLine: { show: true, lineStyle: { color: "rgba(0,0,0,0.6)" } },
        min: min ?? "dataMin",
        max: max ?? "dataMax",
        axisLabel: {
          fontSize: 12,
          formatter: (value: number) => {
            // 根据数值大小自动格式化
            if (value === 0) return "0";
            const absValue = Math.abs(value);
            if (absValue >= 1000) {
              // 大于等于 1000，不保留小数
              return String(Math.round(value));
            } else if (absValue >= 100) {
              // 100-1000，保留 1 位小数
              return value.toFixed(1);
            } else if (absValue >= 10) {
              // 10-100，保留 2 位小数
              return value.toFixed(2);
            } else if (absValue >= 1) {
              // 1-10，保留 3 位小数
              return value.toFixed(3);
            } else if (absValue >= 0.000001) {
              // 0.000001-1，保留 6 位小数，但去除末尾多余的 0
              return value.toFixed(6).replace(/\.?0+$/, "");
            } else {
              // 小于 0.000001，保留 12 位小数，但去除末尾多余的 0
              return value.toFixed(12).replace(/\.?0+$/, "");
            }
          },
        },
      },
      series, // 多条线
      responsive: true,
      animationDuration: 800,
      animationEasing: "cubicOut",
    };
  }, [chartData, multiTaskData, title, currentMode, min, max, grid, extraText, unit]);

  return (
    <div className="line-chart-container" style={{ width, height: typeof height === 'number' ? `${height}px` : height }}>
      <ReactECharts
        option={chartOption}
        style={{ width: "100%", height: "100%" }}
        opts={{ renderer: "canvas" }}
        notMerge={true}
      />
    </div>
  );
};

export default LineChartComponent;